
import React, { useCallback, useEffect, useState } from "react";
import { useParams, Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { Helmet } from "react-helmet";
import Layout from "@/components/layout/Layout";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { NursingHome } from "@/hooks/use-nursing-home";
import { OwnerPageLoadingState } from "@/components/nursing-homes/owner/LoadingState";
import { UnauthorizedState } from "@/components/nursing-homes/owner/UnauthorizedState";
import { OwnerNursingHomeAccessState } from "@/components/nursing-homes/owner/OwnerNursingHomeAccessState";
import { OwnerPageErrorState } from "@/components/nursing-homes/owner/ErrorState";
import { SidebarNav } from "@/components/nursing-homes/owner/dashboard/SidebarNav";
import { SidebarProvider } from "@/components/ui/sidebar";
import { isNeedsInfoClaimStatus, isPendingClaimStatus, isRejectedClaimStatus } from "@/lib/claim-status";

type GateState =
  | "loading"
  | "approved"
  | "no_claim"
  | "pending"
  | "rejected"
  | "needs_info";

export default function OwnerNursingHomePage() {
  const { nhSlug } = useParams();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const [nursingHome, setNursingHome] = useState<NursingHome | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [gate, setGate] = useState<GateState>("loading");
  const [error, setError] = useState<Error | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [centerName, setCenterName] = useState<string | undefined>();
  const [rejectedNotes, setRejectedNotes] = useState<string | null>(null);
  const [needsInfoClaimId, setNeedsInfoClaimId] = useState<string | null>(null);

  const loadNursingHomeGate = useCallback(async () => {
    if (!user || !nhSlug) return;

    try {
      setIsLoading(true);
      setError(null);
      setGate("loading");
      setNursingHome(null);
      setCenterName(undefined);
      setRejectedNotes(null);
      setNeedsInfoClaimId(null);

      const { data: claim, error: claimError } = await supabase
        .from("nursing_home_claims")
        .select("*")
        .eq("user_id", user.id)
        .eq("nursing_home_id", nhSlug)
        .maybeSingle();

      if (claimError) throw claimError;

      if (!claim) {
        setGate("no_claim");
        setNursingHome(null);
        return;
      }

      if (isRejectedClaimStatus(claim.status)) {
        setGate("rejected");
        setRejectedNotes(claim.notes ?? null);
        const { data: homeMeta } = await supabase
          .from("nursing_homes")
          .select("center_name")
          .eq("nh_slug", nhSlug)
          .maybeSingle();
        setCenterName(homeMeta?.center_name ?? undefined);
        setNursingHome(null);
        return;
      }

      if (isNeedsInfoClaimStatus(claim.status)) {
        setGate("needs_info");
        setNeedsInfoClaimId(claim.id);
        setRejectedNotes(claim.notes ?? null);
        const { data: homeMeta } = await supabase
          .from("nursing_homes")
          .select("center_name")
          .eq("nh_slug", nhSlug)
          .maybeSingle();
        setCenterName(homeMeta?.center_name ?? undefined);
        setNursingHome(null);
        return;
      }

      if (isPendingClaimStatus(claim.status)) {
        setGate("pending");
        const { data: homeMeta } = await supabase
          .from("nursing_homes")
          .select("center_name")
          .eq("nh_slug", nhSlug)
          .maybeSingle();
        setCenterName(homeMeta?.center_name ?? undefined);
        setNursingHome(null);
        return;
      }

      if (claim.status === "approved") {
        const { data: home, error: homeError } = await supabase
          .from("nursing_homes")
          .select("*")
          .eq("nh_slug", nhSlug)
          .single();

        if (homeError) throw homeError;

        setNursingHome(home);
        setGate("approved");
        return;
      }

      setGate("no_claim");
      setNursingHome(null);
    } catch (err) {
      console.error("Error fetching nursing home data:", err);
      setError(err instanceof Error ? err : new Error("Unknown error"));
      toast.error("Failed to load nursing home data");
    } finally {
      setIsLoading(false);
    }
  }, [user, nhSlug]);

  useEffect(() => {
    if (user && !authLoading && nhSlug) {
      loadNursingHomeGate();
    }
  }, [user, authLoading, nhSlug, loadNursingHomeGate]);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/profile")) setActiveTab("profile");
    else if (path.includes("/media")) setActiveTab("media");
    else if (path.includes("/leads")) setActiveTab("leads");
    else if (path.includes("/staff")) setActiveTab("staff");
    else if (path.includes("/calendar")) setActiveTab("calendar");
    else if (path.includes("/settings")) setActiveTab("settings");
    else setActiveTab("overview");
  }, [location.pathname]);

  if (!authLoading && !user) {
    return <Navigate to={`/auth/login?redirectTo=/owner/nursing-homes/${nhSlug}`} />;
  }

  if (isLoading || authLoading) {
    return <OwnerPageLoadingState />;
  }

  if (gate === "pending") {
    return (
      <OwnerNursingHomeAccessState variant="pending" centerName={centerName} />
    );
  }

  if (gate === "rejected") {
    return (
      <OwnerNursingHomeAccessState
        variant="rejected"
        centerName={centerName}
        claimNotes={rejectedNotes}
      />
    );
  }

  if (gate === "needs_info") {
    return (
      <OwnerNursingHomeAccessState
        variant="needs_info"
        centerName={centerName}
        claimNotes={rejectedNotes}
        needsInfoClaimId={needsInfoClaimId}
        onNeedsInfoReplySubmitted={loadNursingHomeGate}
      />
    );
  }

  if (gate === "no_claim") {
    return <UnauthorizedState />;
  }

  if (error || !nursingHome) {
    return <OwnerPageErrorState error={error} />;
  }

  return (
    <Layout>
      <Helmet>
        <title>{nursingHome.center_name} - Owner Management | Grandpal</title>
      </Helmet>

      <div className="container p-0 max-w-7xl">
        <SidebarProvider>
          <div className="flex min-h-[calc(100vh-80px)] w-full">
            <SidebarNav activeTab={activeTab} nursingHomeSlug={nhSlug} />
            <div className="flex-1 p-6">
              <Outlet context={{ nursingHome }} />
            </div>
          </div>
        </SidebarProvider>
      </div>
    </Layout>
  );
}
